import {useMutation, useQuery} from '@tanstack/react-query';
import {
  IAssignmentDataResponse,
  ICreateByIdResponse,
  IAssignmentGroupNode,
  ICompletedAssignmentTree,
  IGetBulkTagResponse,
  IDeleteAssignmentGroupQuery,
  ICreateAssignmentResponse,
  IUnAssignedUsers,
  IGeneralResponse,
  IAddAssignedReviewers,
  IGetBulkTagsDetails,
  IEditAssignmentGroupResponse,
  IEditAssignmentGroupByIdResponse,
  IEditAssignmentByIdResponse,
  IQuickAssign,
  IQuickAssignParams,
  IBulkTagSelectedDocCountParams,
  IBulkTagSelectedDocCountResponse,
  IBulkActionPopupResponse,
  IBulkActionPopupNonBgResponse,
  IDoActionResponse,
  IUnAssignUsersResponse,
  IUnAssignUsersRequest,
  ICompleteUncompleteAllDocs,
  IRemoveDocumentsResponse,
  IDoActionNonBgResponse,
  ICompleteUncompleteAllDocsResponse,
  ICopyAssignmentResponse,
  ICompleteIncompleteDocsPayload,
  IMoveAssignmentGroupRequest,
  IBulkAssignDocListIdResponse,
  IBulkActionLikePopupResponse,
  IBulkActionNonbgLikePopupResponse,
  IBulkActionAllDocumentCountResponse,
  IBulkActionAllDocumentCountRequest,
  IQuickCreateResponse,
  ILikeDocumentCountRequest,
  ILikeDocumentCountResponse,
} from '../../api/types/assignment.type';
import {
  getAssignmentList,
  getAssignmentTreeView,
  getCompleteAssignmetTree,
  getCreateById,
  getBulkTagList,
  deleteAssignmentGroup,
  createAssignment,
  updateAssignment,
  editAssignmentById,
  getDistributedDocs,
  getUnAssignedUsers,
  addAssignedReviewers,
  getBulkTagsDetails,
  editAssignmentGroup,
  getEditAssignmentGroupById,
  distributeAssignment,
  getQuickAssign,
  getBulkTagSelectedDocCount,
  callBulkActionsPopup,
  callBulkActionsPopupNonBg,
  doAction,
  unAssignUser,
  completeUncompleteAllDocs,
  removeDocuments,
  doActionNonBg,
  copyAssignment,
  incompleteDocs,
  completeDocs,
  moveAssignmentGroup,
  getBulkAssignDocListId,
  callBulkActionsLikePopup,
  callBulkActionsLikePopupNonbg,
  getBulkActionAllDocumentCount,
  getReassignUserList,
  documentReassign,
  quickCreate,
  getLikeDocumentCountNonBG,
} from '../../api/endpoints/assignmentsApi';
import {
  DocumentAssignmentReqPayload,
  GetReassignUsersListReqPayload,
  ReviewerAssignmentResponse,
} from '../../api/types/reportingApi.type';

export const useAssignmentList = (
  queryParams: string,
  enabled: boolean = true
) => {
  return useQuery<IAssignmentDataResponse, Error>({
    queryKey: ['assignments', queryParams],
    queryFn: () => getAssignmentList(queryParams),
    enabled,
  });
};

export const useCreateLandingData = (queryParams: string, enabled: boolean) => {
  return useQuery<ICreateByIdResponse, Error>({
    queryKey: ['createById', queryParams],
    queryFn: () => getCreateById(queryParams),
    enabled,
  });
};

export const useAssignmentTree = (queryParams: number) => {
  return useQuery<IAssignmentGroupNode[], Error>({
    queryKey: ['assignmentsTree', queryParams],
    queryFn: () => getAssignmentTreeView(queryParams),
  });
};

export const useGetBulkTagList = (queryParams: string) => {
  return useQuery<IGetBulkTagResponse, Error>({
    queryKey: ['actionType', queryParams],
    queryFn: () => getBulkTagList(queryParams),
  });
};

export const useCompleteAssignmentTree = (queryParams: number) => {
  return useQuery<ICompletedAssignmentTree, Error>({
    queryKey: ['completeAssignmentsTree', queryParams],
    queryFn: () => getCompleteAssignmetTree(queryParams),
  });
};

export const useDeleteAssignmentGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<string, Error, IDeleteAssignmentGroupQuery>({
    mutationFn: deleteAssignmentGroup,
    mutationKey: ['deleteAssignmentGroup'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCompleteUncompleteAllDocs = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ICompleteUncompleteAllDocsResponse,
    Error,
    ICompleteUncompleteAllDocs
  >({
    mutationFn: completeUncompleteAllDocs,
    mutationKey: ['completeUncompleteAllDocs'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCreateAssignment = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ICreateAssignmentResponse, Error, any>({
    mutationFn: createAssignment,
    mutationKey: ['createAssignment'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetDistributedDocs = (queryParams: number) => {
  return useQuery<ICreateByIdResponse, Error>({
    queryKey: ['getDistributedDocs', queryParams],
    queryFn: () => getDistributedDocs(queryParams),
  });
};

export const useGetUnAssignedUsers = (queryParams?: number) => {
  return useQuery<IUnAssignedUsers, Error>({
    queryKey: ['getUnAssignedUsers', queryParams],
    queryFn: () => getUnAssignedUsers(queryParams),
  });
};

export const useAddAssignedReviewers = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IGeneralResponse, Error, IAddAssignedReviewers>({
    mutationFn: addAssignedReviewers,
    mutationKey: ['addAssignedReviewers'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUnAssignUser = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IUnAssignUsersResponse, Error, IUnAssignUsersRequest>({
    mutationFn: unAssignUser,
    mutationKey: ['useUnAssignUser'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useRemoveDocuments = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IRemoveDocumentsResponse, Error, string>({
    mutationFn: removeDocuments,
    mutationKey: ['removeDocuments'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDistributeAssignment = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ICreateAssignmentResponse, Error, any>({
    mutationFn: distributeAssignment,
    mutationKey: ['distributeAssignment'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetBulkTagsDetails = () => {
  return useQuery<IGetBulkTagsDetails, Error>({
    queryKey: ['getBulkTagsDetails'],
    queryFn: () => getBulkTagsDetails(),
  });
};

export const useGetAssignmentGroupById = (
  queryParams: string,
  enabled: boolean
) => {
  return useQuery<IEditAssignmentGroupByIdResponse, Error>({
    queryKey: ['getEditAssignmentGroupById', queryParams],
    queryFn: () => getEditAssignmentGroupById(queryParams),
    enabled,
  });
};

export const useEditAssignmentGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IEditAssignmentGroupResponse, Error, any>({
    mutationFn: editAssignmentGroup,
    mutationKey: ['editAssignmentGroup'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUpdateAssignment = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ICreateAssignmentResponse, Error, any>({
    mutationFn: updateAssignment,
    mutationKey: ['updateAssignment'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useEditAssignmentById = (
  queryParams: string,
  enabled: boolean
) => {
  return useQuery<IEditAssignmentByIdResponse, Error>({
    queryKey: ['editAssignmentById', queryParams],
    queryFn: () => editAssignmentById(queryParams),
    enabled,
  });
};

export const useGetQuickAssign = (queryParams: IQuickAssignParams) => {
  return useQuery<IQuickAssign, Error>({
    queryKey: ['getQuickAssign', queryParams],
    queryFn: () => getQuickAssign(queryParams),
  });
};

export const useGetBulkTagSelectedDocCount = (
  queryParams: IBulkTagSelectedDocCountParams,
  enabled: boolean
) => {
  return useQuery<IBulkTagSelectedDocCountResponse, Error>({
    queryKey: ['getBulkTagSelectedDocCount', queryParams],
    queryFn: () => getBulkTagSelectedDocCount(queryParams),
    enabled,
    refetchInterval: (query) => {
      const data = query.state.data as
        | IBulkTagSelectedDocCountResponse
        | undefined;
      if (!data) return 3000; // keep polling until first response
      return data.docCount > 0 ? false : 3000;
    },
    refetchIntervalInBackground: true,
  });
};

export const useCallBulkActionsPopup = (
  onSuccess?: (data: IBulkActionPopupResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IBulkActionPopupResponse, Error, any>({
    mutationFn: callBulkActionsPopup,
    mutationKey: ['callBulkActionsPopup'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCallBulkActionsPopupNonBg = (
  onSuccess?: (data: IBulkActionPopupNonBgResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IBulkActionPopupNonBgResponse, Error, any>({
    mutationFn: callBulkActionsPopupNonBg,
    mutationKey: ['callBulkActionsPopupNonBg'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDoAction = (
  onSuccess?: (data: IDoActionResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IDoActionResponse, Error, any>({
    mutationFn: doAction,
    mutationKey: ['doAction'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDoActionNonBg = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IDoActionNonBgResponse, Error, any>({
    mutationFn: doActionNonBg,
    mutationKey: ['doActionNonBg'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCopyAssignment = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ICopyAssignmentResponse, Error, any>({
    mutationFn: copyAssignment,
    mutationKey: ['copyAssignment'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCompleteDocs = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ICompleteUncompleteAllDocsResponse,
    Error,
    ICompleteIncompleteDocsPayload
  >({
    mutationFn: completeDocs,
    mutationKey: ['completeDocs'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useIncompleteDocs = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ICompleteUncompleteAllDocsResponse,
    Error,
    ICompleteIncompleteDocsPayload
  >({
    mutationFn: incompleteDocs,
    mutationKey: ['incompleteDocs'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useMoveAssignmentGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, IMoveAssignmentGroupRequest>({
    mutationFn: moveAssignmentGroup,
    mutationKey: ['moveAssignmentGroup'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetBulkAssignDocListId = (
  onSuccess?: (data: IBulkAssignDocListIdResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IBulkAssignDocListIdResponse, Error, any>({
    mutationFn: getBulkAssignDocListId,
    mutationKey: ['getBulkAssignDocListId'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCallBulkActionsLikePopup = (
  onSuccess?: (response: IBulkActionLikePopupResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IBulkActionLikePopupResponse, Error, any>({
    mutationFn: callBulkActionsLikePopup,
    mutationKey: ['callBulkActionsLikePopup'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCallBulkActionsLikePopupNonbg = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IBulkActionNonbgLikePopupResponse, Error, any>({
    mutationFn: callBulkActionsLikePopupNonbg,
    mutationKey: ['callBulkActionsLikePopupNonbg'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetBulkActionAllDocumentCount = (
  queryParams: IBulkActionAllDocumentCountRequest,
  enabled: boolean
) => {
  return useQuery<IBulkActionAllDocumentCountResponse, Error>({
    queryKey: ['getBulkActionAllDocumentCount', queryParams],
    queryFn: () => getBulkActionAllDocumentCount(queryParams),
    enabled,
  });
};

export const useGetReassignUserList = (
  onSuccess?: (response: ReviewerAssignmentResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ReviewerAssignmentResponse,
    Error,
    GetReassignUsersListReqPayload
  >({
    mutationFn: getReassignUserList,
    mutationKey: ['getReassignUserList'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCallDocumentReassign = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, DocumentAssignmentReqPayload>({
    mutationFn: documentReassign,
    mutationKey: ['documentReassign'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useQuickCreate = (
  onSuccess?: (response: IQuickCreateResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IQuickCreateResponse, Error, any>({
    mutationFn: quickCreate,
    mutationKey: ['quickCreate'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetLikeDocumentCountNonBG = (
  payload: ILikeDocumentCountRequest | null,
  enabled: boolean
) => {
  return useQuery<ILikeDocumentCountResponse, Error>({
    queryKey: ['getLikeDocumentCountNonBG', payload],
    queryFn: () => {
      if (!payload) {
        throw new Error('Payload is required for getLikeDocumentCountNonBG');
      }
      return getLikeDocumentCountNonBG(payload);
    },
    enabled: enabled && payload !== null,
  });
};
